import { useRef, useCallback } from 'react';

/**
* Custom React hook to preserve and restore vertical scroll positions
* for one or multiple AG Grid instances on the same page.
*
* This is especially useful when grids are unmounted/remounted, data is refreshed,
* or the user navigates away and returns. Scroll positions are tracked per grid
* using a unique `gridName`.
*
* Limitations:
* - Works for client-side row model; infinite/server-side row models require loaded rows.
* - Restoration is approximate row-based using `api.ensureIndexVisible`.
* - Pixel-perfect scroll is not possible because modern AgGridReact no longer exposes `eGridDiv`.
*
* @example
* const { saveScrollRange, restoreScrollRange } = useGridPreserveScroll();
* const playlistGridRef = useRef(null);
*
* <OpDataGrid
*   ref={playlistGridRef}
*   onBodyScrollEnd={(params) => saveScrollRange('playlistGrid', params)}
*   onGridReady={() => restoreScrollRange('playlistGrid', playlistGridRef)}
* />
*/

/* eslint-disable import/prefer-default-export */
export const useGridPreserveScroll = () => {
  const scrollRangeRef = useRef({});

  /**
  * Save the current vertical scroll position for a specific grid.
  *
  * @param {string} gridName - Unique name for the grid instance.
  * @param {Object} params - Event parameters from AG Grid event handlers.
  */
  const saveScrollRange = useCallback((gridName, params) => {
    if (!gridName || !params?.api) return;

    scrollRangeRef.current[gridName] = params.api.getVerticalPixelRange();
  }, []);

  /**
  * Restore the vertical scroll position for a specific grid.
  *
  * Scroll restoration is row-based using `ensureIndexVisible`.
  * Iterates all rows and scrolls to the first row whose `rowTop` is
  * greater than or equal to the saved scroll position.
  *
  * @param {string} gridName - Unique name for the grid instance.
  * @param {React.RefObject} gridRef - Ref pointing to the AgGridReact component.
  */
  const restoreScrollRange = useCallback((gridName, gridRef) => {
    requestAnimationFrame(() => {
      if (!gridRef?.current?.api || !gridName) return;
      const { api } = gridRef.current;

      const scrollRange = scrollRangeRef.current[gridName];
      if (!scrollRange) return;

      let found = false;

      api?.forEachNode((node) => {
        if ((node?.rowTop || 0) >= scrollRange?.top && !found) {
          api?.ensureIndexVisible(node?.rowIndex, 'top');
          found = true;
        }
      });
    });
  }, []);

  return { saveScrollRange, restoreScrollRange };
};
